import React, { createContext, useContext, useState } from "react";
import { Dialog } from "../../design/dialog";
import { FinancialItemType } from "../../models/FinancialItemType";
import { FinancialItemForm } from "../../models/Forms/FinancialItemForm";
import ChooseFinancialItemTypeStep from "./components/ChooseFinancialItemTypeStep";
import ChooseZillowPropertyStep from "./components/ChooseZillowPropertyStep";
import { ZillowProperty } from "../../services/ZillowService";
import FinancialItemFormComponent from "./components/FinancialItemFormComponent";
import { financialItemFormDefaults } from "../../constants/financialItemFormDefaults";
import ChooseIntegrationStep from "./components/ChooseIntegrationStep";
import ChooseFinancialAccountStep from "./components/ChooseFinancialAccountStep";
import { useFinancialItem } from "../../contexts/FinancialItemContext";
import { FinancialItemCategory } from "../../models/FinancialItemCategory";

interface Context {
  openDialog: (
    category: FinancialItemCategory,
    type?: FinancialItemType,
  ) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

const CreateFinancialItemDialogProviderContext = createContext<
  Context | undefined
>(undefined);

export const CreateFinancialItemDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { createFinancialItem } = useFinancialItem(); // Assuming you have a context or service for financial items
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<FinancialItemForm>({});
  const [step, setStep] = useState<
    "type" | "form" | "zillow" | "integration" | "financial_accounts"
  >("type");

  const openDialog = (
    category: FinancialItemCategory,
    type?: FinancialItemType,
  ) => {
    setForm({
      type: type,
      category,
      dateValued: new Date(),
    });
    setStep(type ? (type === "real_estate" ? "zillow" : "form") : "type");
    setIsOpen(true);
  };

  const handleSelectType = (selectedType: FinancialItemType) => {
    setForm((prev) => ({
      ...prev,
      type: selectedType,
    }));
    const defaultFinancialItem = financialItemFormDefaults[selectedType] || {};
    setForm((prev) => ({ ...prev, ...defaultFinancialItem }));
    setStep(selectedType === "real_estate" ? "zillow" : "integration");
  };

  const handlePropertySelected = (property: ZillowProperty | null) => {
    if (property) {
      setForm((prev) => ({
        ...prev,
        name: property.address,
        description: property.address,
        amount: property.amount,
        currency: "USD", // Assuming USD, adjust as needed
        dateValued: new Date(),
        zillowPropertyId: property.zpid,
        zillowPropertyAddress: property.address,
        zillowPropertyValue: property.amount,
        zillowPropertyLastUpdated: new Date(),
      }));
    }
    setStep("form");
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    try {
      await createFinancialItem(form);
      closeDialog();
    } catch (error) {
      console.error("Error creating financial item:", error);
      alert("Failed to create financial item. Please try again later.");
    }
  };

  return (
    <CreateFinancialItemDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      <Dialog open={isOpen} onClose={closeDialog}>
        {step === "type" && (
          <ChooseFinancialItemTypeStep
            setType={handleSelectType}
            onClose={closeDialog}
          />
        )}
        {step === "zillow" && (
          <ChooseZillowPropertyStep
            onPropertySelected={handlePropertySelected}
            onClose={() => setStep("type")}
          />
        )}
        {step === "integration" && (
          <ChooseIntegrationStep
            onClose={() => setStep("type")}
            onSelectManual={() => setStep("form")}
            onSelectPlaid={() => setStep("financial_accounts")}
          />
        )}
        {step === "financial_accounts" && (
          <ChooseFinancialAccountStep
            onSelectFinancialAccount={(account) => {
              setForm((prev) => ({
                ...prev,
                financialAccount: account._id,
              }));
              setStep("form");
            }}
            onClose={() => setStep("integration")}
          />
        )}
        {step === "form" && (
          <FinancialItemFormComponent
            form={form}
            handleUpdateForm={setForm}
            errors={{}}
            onBack={() =>
              setStep(form.type === "real_estate" ? "zillow" : "integration")
            }
            onSubmit={handleSubmit}
          />
        )}
      </Dialog>
      {children}
    </CreateFinancialItemDialogProviderContext.Provider>
  );
};

export const useCreateFinancialItemDialog = () => {
  const ctx = useContext(CreateFinancialItemDialogProviderContext);
  if (!ctx)
    throw new Error(
      "useCreateFinancialItemDialog must be used inside CreateFinancialItemDialogProvider",
    );
  return ctx;
};
